import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/utils";
import { TrendingUp, MousePointer, ArrowRight, DollarSign } from "lucide-react";

export function AffDashboardPage() {
  const [range, setRange] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ["aff-portal", "dashboard", range],
    queryFn: async () => {
      const res = await api.get("/v1/portal/affiliate/dashboard");
      if (!res.ok) throw new Error("Failed to load dashboard");
      const json = await res.json();
      return json.data;
    },
  });

  const { data: timeSeries } = useQuery({
    queryKey: ["aff-portal", "time-series", range],
    queryFn: async () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - range).toISOString().split("T")[0];
      const to = now.toISOString().split("T")[0];
      const res = await api.post("/v1/portal/affiliate/time-series", { from, to });
      if (!res.ok) throw new Error("Failed to load time series");
      return (await res.json()).data || [];
    },
  });

  const stats = data || { clicks: 0, conversions: 0, revenue: 0, payout: 0 };
  const cr = stats.clicks > 0 ? (stats.conversions / stats.clicks) * 100 : 0;

  const cards = [
    { label: "Clicks", value: formatNumber(stats.clicks), icon: MousePointer, color: "text-blue-600 bg-blue-50" },
    { label: "Conversions", value: formatNumber(stats.conversions), icon: ArrowRight, color: "text-emerald-600 bg-emerald-50" },
    { label: "Earnings", value: formatCurrency(stats.payout), icon: DollarSign, color: "text-green-600 bg-green-50" },
    { label: "Conv. Rate", value: formatPercent(cr), icon: TrendingUp, color: "text-purple-600 bg-purple-50" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Last {range} days performance overview</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">{c.label}</span>
                <div className={`p-2 rounded-lg ${c.color}`}>
                  <c.icon className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {timeSeries && timeSeries.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Daily Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Date</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Clicks</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Conversions</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Revenue</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Earnings</th>
                </tr>
              </thead>
              <tbody>
                {timeSeries.slice(0, 15).map((row: { period: string; clicks: number; conversions: number; revenue: number; payout: number }) => (
                  <tr key={row.period} className="border-b border-gray-50">
                    <td className="py-2 px-3 text-gray-700">{row.period.substring(0, 10)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{formatNumber(row.clicks)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{formatNumber(row.conversions)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(row.revenue)}</td>
                    <td className="py-2 px-3 text-right font-medium text-emerald-600">{formatCurrency(row.payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
